/**
 * useAnalytics Composable
 * 
 * Vue composable for integrating analytics tracking into components.
 * Provides reactive context injection, automatic page view tracking,
 * and typed methods for all analytics event categories.
 */

import { computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { analyticsService } from '@/services/analytics/analyticsService';
import type { EcommerceItem } from '@/services/analytics/eventTypes';
import { useProjectStore } from '@/store/project.store';
import { useUserStore } from '@/store/user.store';
import { useFeaturesStore } from '@/store/features.store';

// ============================================================================
// Types
// ============================================================================

export interface UseAnalyticsOptions {
  /** Automatically track page views on route change */
  trackPageViews?: boolean;
  /** Enable debug logging */
  debug?: boolean;
  /** Custom measurement ID (overrides default) */
  measurementId?: string;
}

export interface AnalyticsContext {
  projectId: string | null;
  userId: string | null;
  userRole: string | null;
  subscriptionTier: string | null;
}

// ============================================================================
// Composable
// ============================================================================

export function useAnalytics(options: UseAnalyticsOptions = {}) {
  const {
    trackPageViews = true,
    debug = import.meta.env.DEV,
  } = options;

  const route = useRoute();
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const featuresStore = useFeaturesStore();

  // ============================================================================
  // Reactive Context
  // ============================================================================

  /** Current project ID from store */
  const projectId = computed(() => projectStore.projectId || null);

  /** Current user ID from store */
  const userId = computed(() => userStore.authUser?.uid || null);

  /** Current user role */
  const userRole = computed(() => userStore.projectRole || null);

  /** Subscription tier for analytics segmentation */
  const subscriptionTier = computed(() => {
    const subscription = projectStore.project_subscriptions?.[0];
    if (!subscription) return 'free';
    // Use subscription type as tier indicator, or check if subscription exists
    return subscription.type || (subscription.subscriptionId ? 'subscribed' : 'free');
  });

  /** Full analytics context */
  const context = computed<AnalyticsContext>(() => ({
    projectId: projectId.value,
    userId: userId.value,
    userRole: userRole.value,
    subscriptionTier: subscriptionTier.value,
  }));

  /** Check if analytics feature is enabled */
  const isAnalyticsEnabled = computed(() => {
    return featuresStore.subscriptionHasFeature('limited_analytics') ||
           featuresStore.subscriptionHasFeature('advanced_analytics');
  });

  // ============================================================================
  // Context Synchronization
  // ============================================================================

  // Watch for project changes and update analytics context
  watch(projectId, (newProjectId) => {
    if (newProjectId) {
      analyticsService.setProjectContext(newProjectId);
      if (debug) {
        console.log('[useAnalytics] Project context updated:', newProjectId);
      }
    }
  }, { immediate: true });

  // Watch for user changes and update analytics context
  watch(userId, (newUserId) => {
    if (newUserId) {
      analyticsService.setUserContext(newUserId, {
        user_role: userRole.value || undefined,
        subscription_tier: subscriptionTier.value,
        project_id: projectId.value || undefined,
      });
      if (debug) {
        console.log('[useAnalytics] User context updated:', newUserId);
      }
    } else {
      analyticsService.clearUserContext();
    }
  }, { immediate: true });

  // ============================================================================
  // Page View Tracking
  // ============================================================================

  let routeUnwatch: (() => void) | null = null;

  onMounted(() => {
    if (trackPageViews) {
      // Track initial page view
      trackPageView();

      // Watch for route changes
      routeUnwatch = watch(
        () => route.fullPath,
        () => {
          trackPageView();
        }
      );
    }
  });

  onUnmounted(() => {
    if (routeUnwatch) {
      routeUnwatch();
    }
  });

  /** Track current page view */
  const trackPageView = () => {
    const pageTitle = document.title || route.name?.toString() || 'Unknown';
    const pageLocation = typeof window !== 'undefined' ? window.location.href : '';
    const pagePath = route.fullPath;

    analyticsService.trackPageView({
      pageTitle,
      pageLocation,
      pagePath,
      projectId: projectId.value || undefined,
    });

    if (debug) {
      console.log('[useAnalytics] Page view tracked:', { pageTitle, pagePath });
    }
  };

  // ============================================================================
  // User Acquisition Events
  // ============================================================================

  const trackUserRegistered = (params: {
    method: 'public_registration' | 'invitation' | 'bulk_import' | 'manual_add';
    autoApproved: boolean;
    registrationSource?: string;
  }) => {
    analyticsService.trackUserRegistered({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackInvitationSent = (params: {
    invitationType: 'email' | 'link' | 'group';
    recipientCount?: number;
  }) => {
    analyticsService.trackInvitationSent({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackInvitationAccepted = (params: {
    invitationId: string;
    timeToAcceptHours?: number;
    invitationType?: string;
  }) => {
    analyticsService.trackInvitationAccepted({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackUserImported = (params: {
    importCount: number;
    source: 'csv' | 'google_sheets' | 'excel' | 'manual';
    sessionId?: string;
  }) => {
    analyticsService.trackUserImported({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackUserAddedManual = (params: {
    role: string;
    addedByRole?: string;
  }) => {
    analyticsService.trackUserAddedManual({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackUserLogin = (params: {
    method: 'email' | 'phone' | 'passkey' | 'otp' | 'descope';
    isReturningUser: boolean;
  }) => {
    analyticsService.trackUserLogin({
      ...params,
      projectId: projectId.value || '',
    });
  };

  // ============================================================================
  // Event Engagement Events
  // ============================================================================

  const trackEventRsvp = (params: {
    eventId: string;
    response: 'accepted' | 'declined' | 'tentative';
    eventTitle?: string;
    daysUntilEvent?: number;
  }) => {
    analyticsService.trackEventRsvp({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackEventPaymentCompleted = (params: {
    eventId: string;
    amount: number;
    currency?: string;
    paymentMethod?: string;
  }) => {
    analyticsService.trackEventPaymentCompleted({
      ...params,
      currency: params.currency || 'USD',
      projectId: projectId.value || '',
    });
  };

  const trackEventViewed = (params: {
    eventId: string;
    eventTitle?: string;
    eventType?: string;
  }) => {
    analyticsService.trackEventViewed({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackCalendarInvitationResponse = (params: {
    eventId: string;
    responseType: 'accept' | 'decline' | 'tentative';
    source?: 'email_link' | 'app';
  }) => {
    analyticsService.trackCalendarInvitationResponse({
      ...params,
      source: params.source || 'app',
      projectId: projectId.value || '',
    });
  };

  // ============================================================================
  // Commerce Events
  // ============================================================================

  const trackViewItem = (params: {
    items: EcommerceItem[];
    currency?: string;
  }) => {
    analyticsService.trackViewItem({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackAddToCart = (params: {
    items: EcommerceItem[];
    currency?: string;
  }) => {
    analyticsService.trackAddToCart({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackRemoveFromCart = (params: {
    items: EcommerceItem[];
    currency?: string;
  }) => {
    analyticsService.trackRemoveFromCart({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackViewCart = (params: {
    items: EcommerceItem[];
    currency?: string;
  }) => {
    analyticsService.trackViewCart({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackBeginCheckout = (params: {
    items: EcommerceItem[];
    currency?: string;
    coupon?: string;
  }) => {
    analyticsService.trackBeginCheckout({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackPurchase = (params: {
    transactionId: string;
    items: EcommerceItem[];
    currency?: string;
    tax?: number;
    shipping?: number;
    coupon?: string;
  }) => {
    analyticsService.trackPurchase({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackDonation = (params: {
    amount: number;
    currency?: string;
    recurring: boolean;
    donationType?: 'one_time' | 'monthly' | 'annual';
    campaign?: string;
  }) => {
    analyticsService.trackDonation({
      ...params,
      projectId: projectId.value || '',
    });
  };

  // ============================================================================
  // Content Engagement Events
  // ============================================================================

  const trackChannelMessageSent = (params: {
    channelId: string;
    messageType?: 'text' | 'image' | 'file';
    hasAttachments?: boolean;
  }) => {
    analyticsService.trackChannelMessageSent({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackMailSent = (params: {
    recipientCount: number;
    mailType: 'individual' | 'broadcast' | 'channel';
    hasAttachments?: boolean;
  }) => {
    analyticsService.trackMailSent({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackBlogPostViewed = (params: {
    postId: string;
    postTitle?: string;
    authorId?: string;
    readTimeSeconds?: number;
  }) => {
    analyticsService.trackBlogPostViewed({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackMediaViewed = (params: {
    mediaId: string;
    mediaType: 'image' | 'video' | 'document' | 'audio';
    fileSizeKb?: number;
  }) => {
    analyticsService.trackMediaViewed({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackDirectorySearched = (params: {
    queryLength: number;
    resultsCount: number;
    searchType?: 'public' | 'map' | 'all';
    filtersApplied?: string[];
  }) => {
    analyticsService.trackDirectorySearched({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackProfileViewed = (params: {
    profileUserId: string;
    viewerRelationship?: 'self' | 'admin' | 'member';
    viewSource?: 'directory' | 'search' | 'direct';
  }) => {
    analyticsService.trackProfileViewed({
      ...params,
      projectId: projectId.value || '',
    });
  };

  // ============================================================================
  // Feature & Engagement Events
  // ============================================================================

  const trackFeatureUsed = (params: {
    featureName: string;
    featureCategory?: string;
    action?: string;
  }) => {
    analyticsService.trackFeatureUsed({
      ...params,
      projectId: projectId.value || '',
    });
  };

  const trackSessionStart = (params?: {
    sessionId?: string;
  }) => {
    analyticsService.trackSessionStart({
      ...params,
      projectId: projectId.value || undefined,
    });
  };

  // ============================================================================
  // Error & Performance Events
  // ============================================================================

  const trackError = (params: {
    errorType: 'api' | 'validation' | 'auth' | 'network' | 'unknown';
    errorMessage: string;
    errorCode?: string;
  }) => {
    analyticsService.trackError({
      ...params,
      projectId: projectId.value || undefined,
      pagePath: route.fullPath,
    });
  };

  const trackPerformance = (params: {
    metricName: string;
    value: number;
  }) => {
    analyticsService.trackPerformance({
      ...params,
      projectId: projectId.value || undefined,
      pagePath: route.fullPath,
    });
  };

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /** Initialize analytics with measurement ID */
  const initialize = (measurementId: string) => {
    analyticsService.initialize(measurementId, {
      debugMode: debug,
    });
  };

  /** Enable or disable analytics */
  const setEnabled = (enabled: boolean) => {
    analyticsService.setEnabled(enabled);
  };

  /** Check if analytics service is ready */
  const isReady = computed(() => analyticsService.isReady());

  /** Get current analytics configuration */
  const getConfig = () => analyticsService.getConfig();

  // ============================================================================
  // Return Public API
  // ============================================================================

  return {
    // Context
    context,
    projectId,
    userId,
    userRole,
    subscriptionTier,
    isAnalyticsEnabled,
    isReady,

    // Initialization
    initialize,
    setEnabled,
    getConfig,

    // Page Views
    trackPageView,

    // User Acquisition
    trackUserRegistered,
    trackInvitationSent,
    trackInvitationAccepted,
    trackUserImported,
    trackUserAddedManual,
    trackUserLogin,

    // Event Engagement
    trackEventRsvp,
    trackEventPaymentCompleted,
    trackEventViewed,
    trackCalendarInvitationResponse,

    // Commerce
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackPurchase,
    trackDonation,

    // Content
    trackChannelMessageSent,
    trackMailSent,
    trackBlogPostViewed,
    trackMediaViewed,
    trackDirectorySearched,
    trackProfileViewed,

    // Engagement
    trackFeatureUsed,
    trackSessionStart,

    // Error & Performance
    trackError,
    trackPerformance,
  };
}

export default useAnalytics;

